import { Property, DateRange } from '../../types';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface FormValidationResult extends ValidationResult {
  fieldErrors: Record<string, string[]>;
}

export interface FinancialFormData {
  propertyIds: string[];
  dateRange: DateRange;
  groupBy?: string;
  breakdown?: string;
  exportFormat?: string;
  reportTemplate?: string;
  emailAddress?: string;
}

/**
 * Validates property selection for financial dashboard
 */
export const validatePropertySelection = (
  properties: Property[],
  selectedProperties: string[]
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if any properties are selected
  if (selectedProperties.length === 0) {
    errors.push('At least one property must be selected');
  }

  // Check if properties array is valid
  if (!Array.isArray(properties)) {
    errors.push('Properties data is invalid');
    return { isValid: false, errors, warnings };
  }

  // Check if selected properties exist and are active
  const activePropertyIds = properties
    .filter(p => p.status === 'active')
    .map(p => p.id);

  const inactivePropertyIds = properties
    .filter(p => p.status === 'inactive')
    .map(p => p.id);

  const invalidSelections = selectedProperties.filter(
    id => !activePropertyIds.includes(id) && !inactivePropertyIds.includes(id)
  );

  const inactiveSelections = selectedProperties.filter(
    id => inactivePropertyIds.includes(id)
  );

  if (invalidSelections.length > 0) {
    errors.push(`Invalid property selections: ${invalidSelections.join(', ')}`);
  }

  if (inactiveSelections.length > 0) {
    warnings.push(`Inactive properties selected: ${inactiveSelections.join(', ')}. Data may be limited.`);
  }

  // Check for reasonable limits
  if (selectedProperties.length > 20) {
    warnings.push('Many properties selected. Dashboard may load slowly.');
  }

  // Check for mixed currencies (if properties have different currencies)
  const selectedPropertiesData = properties.filter(p => 
    selectedProperties.includes(p.id)
  );
  
  const currencies = [...new Set(selectedPropertiesData.map(p => p.currency))];
  if (currencies.length > 1) {
    warnings.push(`Mixed currencies detected: ${currencies.join(', ')}. Financial calculations may require currency conversion.`);
  }

  // Check for duplicate selections
  const uniqueSelections = [...new Set(selectedProperties)];
  if (uniqueSelections.length !== selectedProperties.length) {
    warnings.push('Duplicate property selections detected and will be ignored.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validates date range selection for financial dashboard
 */
export const validateDateRange = (
  dateRange: DateRange,
  minDate?: Date,
  maxDate?: Date
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const { startDate, endDate } = dateRange;

  // Check if dates are provided
  if (!startDate) {
    errors.push('Start date is required');
  }

  if (!endDate) {
    errors.push('End date is required');
  }

  if (!startDate || !endDate) {
    return { isValid: false, errors, warnings };
  }

  // Validate date objects
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) {
    errors.push('Start date must be a valid date');
  }

  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) {
    errors.push('End date must be a valid date');
  }

  if (errors.length > 0) {
    return { isValid: false, errors, warnings };
  }

  // Check date order
  if (startDate > endDate) {
    errors.push('Start date must be before or equal to end date');
  }

  // Check against min/max dates
  if (minDate && startDate < minDate) {
    errors.push(`Start date cannot be before ${minDate.toLocaleDateString()}`);
  }

  if (maxDate && endDate > maxDate) {
    errors.push(`End date cannot be after ${maxDate.toLocaleDateString()}`);
  }

  // Check for reasonable date range (not too large)
  const daysDifference = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysDifference > 1095) { // 3 years
    errors.push('Date range cannot exceed 3 years');
  }

  if (daysDifference > 730) { // 2 years
    warnings.push('Large date range selected. Dashboard may load slowly.');
  }

  if (daysDifference < 1) {
    errors.push('Date range must be at least 1 day');
  }

  // Check for future dates (if maxDate is today)
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today

  if (!maxDate || maxDate <= today) {
    if (startDate > today) {
      warnings.push('Start date is in the future - no data may be available');
    }
    if (endDate > today) {
      warnings.push('End date is in the future - data may be incomplete');
    }
  }

  // Check for very old dates
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  if (endDate < oneYearAgo) {
    warnings.push('Selected date range is more than a year old. Data may be archived.');
  }

  // Check for weekend-only or holiday periods that might have limited data
  const isWeekendOnly = daysDifference <= 7 && 
    (startDate.getDay() === 0 || startDate.getDay() === 6) &&
    (endDate.getDay() === 0 || endDate.getDay() === 6);
  
  if (isWeekendOnly) {
    warnings.push('Selected date range appears to be weekend-only. Revenue patterns may differ.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validates combined property and date selection
 */
export const validateFinancialDashboardInputs = (
  properties: Property[],
  selectedProperties: string[],
  dateRange: DateRange,
  minDate?: Date,
  maxDate?: Date
): ValidationResult => {
  const propertyValidation = validatePropertySelection(properties, selectedProperties);
  const dateValidation = validateDateRange(dateRange, minDate, maxDate);

  return {
    isValid: propertyValidation.isValid && dateValidation.isValid,
    errors: [...propertyValidation.errors, ...dateValidation.errors]
  };
};

/**
 * Utility to get default date range (current month)
 */
export const getDefaultDateRange = (): DateRange => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    startDate: startOfMonth,
    endDate: endOfMonth,
    preset: 'this_month'
  };
};

/**
 * Utility to format date range for display
 */
export const formatDateRangeForDisplay = (dateRange: DateRange): string => {
  const { startDate, endDate, preset } = dateRange;
  
  const dateStr = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  
  if (preset && preset !== 'custom') {
    return `${dateStr} (${preset.replace(/_/g, ' ')})`;
  }
  
  return dateStr;
};

/**
 * Utility to calculate the number of days in a date range
 */
export const calculateDateRangeDays = (dateRange: DateRange): number => {
  return Math.ceil(
    (dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
  ) + 1; // +1 to include both start and end dates
};
/*
*
 * Validates export form parameters
 */
export const validateExportForm = (formData: {
  format: string;
  dateRange: DateRange;
  propertyIds: string[];
  includeCharts?: boolean;
  sections?: string[];
}): FormValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  // Validate format
  const validFormats = ['csv', 'excel', 'pdf'];
  if (!formData.format) {
    fieldErrors.format = ['Export format is required'];
    errors.push('Export format is required');
  } else if (!validFormats.includes(formData.format.toLowerCase())) {
    fieldErrors.format = [`Invalid format. Must be one of: ${validFormats.join(', ')}`];
    errors.push('Invalid export format');
  }

  // Validate date range
  const dateValidation = validateDateRange(formData.dateRange);
  if (!dateValidation.isValid) {
    fieldErrors.dateRange = dateValidation.errors;
    errors.push(...dateValidation.errors);
  }
  if (dateValidation.warnings) {
    warnings.push(...dateValidation.warnings);
  }

  // Validate property selection
  if (!formData.propertyIds || formData.propertyIds.length === 0) {
    fieldErrors.propertyIds = ['At least one property must be selected'];
    errors.push('At least one property must be selected');
  } else if (formData.propertyIds.length > 50) {
    fieldErrors.propertyIds = ['Cannot export more than 50 properties at once'];
    errors.push('Too many properties selected for export');
  }

  // Validate sections if provided
  if (formData.sections && formData.sections.length > 0) {
    const validSections = ['summary', 'revenue', 'expenses', 'profit_loss', 'charts', 'details'];
    const invalidSections = formData.sections.filter(s => !validSections.includes(s));
    if (invalidSections.length > 0) {
      fieldErrors.sections = [`Invalid sections: ${invalidSections.join(', ')}`];
      errors.push('Invalid export sections selected');
    }
  }

  // Performance warnings
  const daysDiff = Math.ceil(
    (formData.dateRange.endDate.getTime() - formData.dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  
  if (daysDiff > 365 && formData.propertyIds.length > 10) {
    warnings.push('Large export requested. Processing may take several minutes.');
  }

  if (formData.format === 'pdf' && formData.includeCharts && formData.propertyIds.length > 5) {
    warnings.push('PDF export with charts for many properties may be very large.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fieldErrors
  };
};

/**
 * Validates report generation form parameters
 */
export const validateReportForm = (formData: {
  template: string;
  dateRange: DateRange;
  propertyIds: string[];
  deliveryMethod: string;
  emailAddress?: string;
  sections?: string[];
}): FormValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  // Validate template
  const validTemplates = ['monthly', 'quarterly', 'annual', 'custom', 'summary'];
  if (!formData.template) {
    fieldErrors.template = ['Report template is required'];
    errors.push('Report template is required');
  } else if (!validTemplates.includes(formData.template)) {
    fieldErrors.template = [`Invalid template. Must be one of: ${validTemplates.join(', ')}`];
    errors.push('Invalid report template');
  }

  // Validate date range
  const dateValidation = validateDateRange(formData.dateRange);
  if (!dateValidation.isValid) {
    fieldErrors.dateRange = dateValidation.errors;
    errors.push(...dateValidation.errors);
  }
  if (dateValidation.warnings) {
    warnings.push(...dateValidation.warnings);
  }

  // Validate property selection
  if (!formData.propertyIds || formData.propertyIds.length === 0) {
    fieldErrors.propertyIds = ['At least one property must be selected'];
    errors.push('At least one property must be selected');
  }

  // Validate delivery method
  const validDeliveryMethods = ['download', 'email'];
  if (!formData.deliveryMethod) {
    fieldErrors.deliveryMethod = ['Delivery method is required'];
    errors.push('Delivery method is required');
  } else if (!validDeliveryMethods.includes(formData.deliveryMethod)) {
    fieldErrors.deliveryMethod = [`Invalid delivery method. Must be one of: ${validDeliveryMethods.join(', ')}`];
    errors.push('Invalid delivery method');
  }

  // Validate email address if email delivery is selected
  if (formData.deliveryMethod === 'email') {
    if (!formData.emailAddress) {
      fieldErrors.emailAddress = ['Email address is required for email delivery'];
      errors.push('Email address is required for email delivery');
    } else {
      const emailValidation = validateEmailAddress(formData.emailAddress);
      if (!emailValidation.isValid) {
        fieldErrors.emailAddress = emailValidation.errors;
        errors.push(...emailValidation.errors);
      }
    }
  }

  // Validate sections if provided
  if (formData.sections && formData.sections.length > 0) {
    const validSections = ['summary', 'revenue', 'expenses', 'profit_loss', 'charts', 'details'];
    const invalidSections = formData.sections.filter(s => !validSections.includes(s));
    if (invalidSections.length > 0) {
      fieldErrors.sections = [`Invalid sections: ${invalidSections.join(', ')}`];
      errors.push('Invalid report sections selected');
    }
  }

  // Template-specific validation
  if (formData.template === 'monthly') {
    const daysDiff = Math.ceil(
      (formData.dateRange.endDate.getTime() - formData.dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff > 31) {
      warnings.push('Monthly template selected but date range exceeds one month.');
    }
  }

  if (formData.template === 'quarterly') {
    const daysDiff = Math.ceil(
      (formData.dateRange.endDate.getTime() - formData.dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysDiff > 93) {
      warnings.push('Quarterly template selected but date range exceeds three months.');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fieldErrors
  };
};

/**
 * Validates email address format
 */
export const validateEmailAddress = (email: string): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!email) {
    errors.push('Email address is required');
    return { isValid: false, errors, warnings };
  }

  if (typeof email !== 'string') {
    errors.push('Email address must be a string');
    return { isValid: false, errors, warnings };
  }

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    errors.push('Invalid email address format');
  }

  // Additional validation
  if (email.length > 254) {
    errors.push('Email address is too long (maximum 254 characters)');
  }

  if (email.includes('..')) {
    errors.push('Email address cannot contain consecutive dots');
  }

  // Check for common typos
  const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
  const domain = email.split('@')[1]?.toLowerCase();
  
  if (domain) {
    const similarDomains = commonDomains.filter(d => 
      Math.abs(d.length - domain.length) <= 2 && 
      d !== domain &&
      (d.includes(domain.substring(0, 3)) || domain.includes(d.substring(0, 3)))
    );
    
    if (similarDomains.length > 0) {
      warnings.push(`Did you mean one of these domains: ${similarDomains.join(', ')}?`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validates user preferences form
 */
export const validateUserPreferencesForm = (preferences: {
  monthlyReportsEnabled?: boolean;
  reportEmail?: string;
  defaultProperties?: string[];
  defaultDateRange?: string;
  chartPreferences?: {
    defaultChartType?: string;
    colorScheme?: string;
  };
}): FormValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  // Validate monthly reports enabled flag
  if (preferences.monthlyReportsEnabled !== undefined && typeof preferences.monthlyReportsEnabled !== 'boolean') {
    fieldErrors.monthlyReportsEnabled = ['Monthly reports enabled must be true or false'];
    errors.push('Invalid monthly reports enabled value');
  }

  // Validate report email if provided
  if (preferences.reportEmail) {
    const emailValidation = validateEmailAddress(preferences.reportEmail);
    if (!emailValidation.isValid) {
      fieldErrors.reportEmail = emailValidation.errors;
      errors.push(...emailValidation.errors);
    }
    if (emailValidation.warnings) {
      warnings.push(...emailValidation.warnings);
    }
  }

  // Validate default properties if provided
  if (preferences.defaultProperties) {
    if (!Array.isArray(preferences.defaultProperties)) {
      fieldErrors.defaultProperties = ['Default properties must be an array'];
      errors.push('Invalid default properties format');
    } else if (preferences.defaultProperties.length > 20) {
      fieldErrors.defaultProperties = ['Cannot set more than 20 default properties'];
      errors.push('Too many default properties');
    }
  }

  // Validate default date range if provided
  if (preferences.defaultDateRange) {
    const validRanges = ['last_30_days', 'last_3_months', 'last_6_months', 'last_year', 'year_to_date', 'this_month'];
    if (!validRanges.includes(preferences.defaultDateRange)) {
      fieldErrors.defaultDateRange = [`Invalid default date range. Must be one of: ${validRanges.join(', ')}`];
      errors.push('Invalid default date range');
    }
  }

  // Validate chart preferences if provided
  if (preferences.chartPreferences) {
    if (typeof preferences.chartPreferences !== 'object') {
      fieldErrors.chartPreferences = ['Chart preferences must be an object'];
      errors.push('Invalid chart preferences format');
    } else {
      const chartPrefs = preferences.chartPreferences;
      
      if (chartPrefs.defaultChartType) {
        const validChartTypes = ['line', 'bar', 'pie', 'area', 'doughnut'];
        if (!validChartTypes.includes(chartPrefs.defaultChartType)) {
          fieldErrors['chartPreferences.defaultChartType'] = [`Invalid chart type. Must be one of: ${validChartTypes.join(', ')}`];
          errors.push('Invalid default chart type');
        }
      }
      
      if (chartPrefs.colorScheme) {
        const validColorSchemes = ['default', 'blue', 'green', 'purple', 'orange', 'red'];
        if (!validColorSchemes.includes(chartPrefs.colorScheme)) {
          warnings.push(`Unknown color scheme: ${chartPrefs.colorScheme}. Using default.`);
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fieldErrors
  };
};

/**
 * Validates dashboard filter form
 */
export const validateDashboardFilters = (
  properties: Property[],
  formData: FinancialFormData
): FormValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  // Validate property selection
  const propertyValidation = validatePropertySelection(properties, formData.propertyIds);
  if (!propertyValidation.isValid) {
    fieldErrors.propertyIds = propertyValidation.errors;
    errors.push(...propertyValidation.errors);
  }
  if (propertyValidation.warnings) {
    warnings.push(...propertyValidation.warnings);
  }

  // Validate date range
  const dateValidation = validateDateRange(formData.dateRange);
  if (!dateValidation.isValid) {
    fieldErrors.dateRange = dateValidation.errors;
    errors.push(...dateValidation.errors);
  }
  if (dateValidation.warnings) {
    warnings.push(...dateValidation.warnings);
  }

  // Validate groupBy if provided
  if (formData.groupBy) {
    const validGroupBy = ['day', 'week', 'month'];
    if (!validGroupBy.includes(formData.groupBy)) {
      fieldErrors.groupBy = [`Invalid groupBy. Must be one of: ${validGroupBy.join(', ')}`];
      errors.push('Invalid groupBy parameter');
    }
  }

  // Validate breakdown if provided
  if (formData.breakdown) {
    const validBreakdowns = ['property', 'source', 'time', 'category'];
    if (!validBreakdowns.includes(formData.breakdown)) {
      fieldErrors.breakdown = [`Invalid breakdown. Must be one of: ${validBreakdowns.join(', ')}`];
      errors.push('Invalid breakdown parameter');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    fieldErrors
  };
};

/**
 * Real-time validation for form fields
 */
export const validateField = (
  fieldName: string,
  value: any,
  context?: { properties?: Property[]; dateRange?: DateRange }
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  switch (fieldName) {
    case 'email':
      return validateEmailAddress(value);
      
    case 'propertyIds':
      if (context?.properties) {
        return validatePropertySelection(context.properties, value || []);
      }
      break;
      
    case 'dateRange':
      return validateDateRange(value);
      
    case 'exportFormat':
      const validFormats = ['csv', 'excel', 'pdf'];
      if (value && !validFormats.includes(value.toLowerCase())) {
        errors.push(`Invalid format. Must be one of: ${validFormats.join(', ')}`);
      }
      break;
      
    case 'reportTemplate':
      const validTemplates = ['monthly', 'quarterly', 'annual', 'custom', 'summary'];
      if (value && !validTemplates.includes(value)) {
        errors.push(`Invalid template. Must be one of: ${validTemplates.join(', ')}`);
      }
      break;
      
    default:
      // Generic validation for common field types
      if (fieldName.includes('email') || fieldName.includes('Email')) {
        return validateEmailAddress(value);
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Sanitizes user input to prevent XSS and other security issues
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .substring(0, 1000); // Limit length
};

/**
 * Validates and sanitizes form data
 */
export const validateAndSanitizeFormData = (
  formData: Record<string, any>
): { sanitized: Record<string, any>; validation: ValidationResult } => {
  const sanitized: Record<string, any> = {};
  const errors: string[] = [];
  const warnings: string[] = [];

  Object.entries(formData).forEach(([key, value]) => {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
      
      // Check if sanitization changed the value
      if (sanitized[key] !== value) {
        warnings.push(`Input for ${key} was sanitized for security`);
      }
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    } else {
      sanitized[key] = value;
    }
  });

  return {
    sanitized,
    validation: {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  };
};